import { SentenceType } from 'types/types'
import { computed } from 'vue'
import { VueLogger } from 'vue-logger-plugin'

import { isSeparator, JOINING_SEPARATORS, normalizeSentenceText } from '@/util/CharacterClasses'
import { SegmentationType } from '@/util/Segmentation'

// ─── Phrase-level constants ──────────────────────────────────────────
// The slider ranges from PHRASE_LEVEL_SENTENCE (full verse) through
// trope-based splits (levels 1–4) to PHRASE_LEVEL_WORDS (every word).
const PHRASE_LEVEL_SENTENCE = 0
const PHRASE_LEVEL_WORDS = 5
// Default trope depth when no specific level is known (e.g. restoring from
// a "phrases" hidden tag, or the default maxLevel for sentenceToSegmentation).
// Level 2 = etnachta + zaqef/segol/revia — a good middle-ground split.
const DEFAULT_TROPE_LEVEL = 2

// ─── Trope (cantillation) hierarchy ─────────────────────────────────
// Disjunctive accents, ordered by strength.  Each level recursively
// subdivides the text produced by the level above.
const MAQAF = '\u05BE'
const ETNACHTA = '\u0591'
const SEGOL = '\u0592'
const SHALSHELET = '\u0593'
const ZAQEF_QATAN = '\u0594'
const ZAQEF_GADOL = '\u0595'
const TIPCHA = '\u0596'
const REVIA = '\u0597'
const ZARQA = '\u0598'
const PASHTA = '\u0599'
const YETIV = '\u059A'
const TEVIR = '\u059B'
const GERESH = '\u059C'
const GERSHAYIM = '\u059E'
const PAZER = '\u05A1'
const PAZER_GADOL = '\u059F'
const TELISHA_GEDOLA = '\u05A0'
const MUNACH_LEGARME = '\u05C0'

enum PHRASERS {
  BMT,
  DEFAULT,
}

// Each entry is one hierarchical level of disjunctive accents.
// Level 0 (etnachta) is the strongest pause; level 3 is the finest.
const SPLITTERS: Record<PHRASERS, string[][]> = {
  [PHRASERS.BMT]: [
    [ETNACHTA],
    [ZAQEF_GADOL, ZAQEF_QATAN, SEGOL, REVIA, GERESH, SHALSHELET],
    [TEVIR, PASHTA, YETIV, ZARQA],
    [PAZER, PAZER_GADOL, TELISHA_GEDOLA, GERSHAYIM, MUNACH_LEGARME],
  ],
  [PHRASERS.DEFAULT]: [
    [ETNACHTA],
    [TIPCHA, ZAQEF_GADOL, ZAQEF_QATAN, SEGOL, SHALSHELET],
    [TEVIR, REVIA, PASHTA, YETIV, ZARQA],
    [PAZER, PAZER_GADOL, TELISHA_GEDOLA, GERESH, GERSHAYIM, MUNACH_LEGARME],
  ],
}

// ─── Pure helpers (no logger, no Vue reactivity) ────────────────────

/** Split `text` at every occurrence of any trope in `splitters`. */
const splitOnTrope = (text: string, splitters: string[]): string[] => {
  const parts: string[] = []
  let lastIndex = 0

  for (let i = 0; i < text.length; i++) {
    if (splitters.includes(text[i])) {
      let breakIndex = i + 1
      // Skip through the rest of the current word (and any maqaf/rafe-joined
      // continuations) so the entire joined phrase stays in one segment.
      while (breakIndex < text.length && (!isSeparator(text[breakIndex]) || JOINING_SEPARATORS.has(text[breakIndex]))) {
        breakIndex++
      }
      parts.push(text.substring(lastIndex, breakIndex))
      lastIndex = breakIndex + 1
    }
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return parts
}

/** Count words in Hebrew text (maqaf-joined tokens count as one word). */
const wordCount = (text: string): number => {
  let count = 0
  let inWord = false

  for (let i = 0; i < text.length; i++) {
    if (!isSeparator(text[i]) || JOINING_SEPARATORS.has(text[i])) {
      if (!inWord) {
        inWord = true
      }
    } else if (inWord) {
      count++
      inWord = false
    }
  }

  if (inWord) {
    count++
  }

  return count
}

/** Recursively split text through hierarchical trope levels. */
function parsePhrase(text: string, method: PHRASERS, level: number, maxLevel: number): string[] {
  if (level >= SPLITTERS[method].length || level >= maxLevel) {
    return [text]
  }
  const segments = splitOnTrope(text, SPLITTERS[method][level])
  return segments.flatMap((segment) => parsePhrase(segment, method, level + 1, maxLevel))
}

/**
 * Convert a sentence to a segmentation array using trope-based splitting.
 * Each element is the word-count for one phrase.
 *
 * @param maxLevel  How many levels of the hierarchy to recurse into (1–4).
 */
const sentenceToSegmentation = (
  sentence: SentenceType,
  method: PHRASERS = PHRASERS.BMT,
  maxLevel: number = DEFAULT_TROPE_LEVEL,
): SegmentationType => {
  if (!sentence.text) {
    return []
  }
  // Normalize to the same canonical form that AnnotatedSentence uses for
  // word splitting, so our word counts agree with the rendering.
  const text = normalizeSentenceText(sentence.text)

  // Short verses rarely have etnachta — skip straight to L1 disjunctives.
  const startLevel = wordCount(text) <= 4 ? 1 : 0
  const segments = parsePhrase(text, method, startLevel, maxLevel)

  // AnnotatedText splits on maqaf; add maqaf count per phrase to compensate.
  return segments.map((segment) => {
    const wc = wordCount(segment)
    const maqafCount = [...segment].filter((char) => char === MAQAF).length
    return wc + maqafCount
  })
}

/**
 * Unified entry point: compute a segmentation for the given phrase level (0–5).
 *
 *   0  → full verse (one segment)
 *   1–4 → trope hierarchy levels (recursive disjunctive splits)
 *   5  → every word
 */
const segmentationForLevel = (sentence: SentenceType, nWords: number, level: number): SegmentationType => {
  if (!sentence.text || nWords === 0) {
    return []
  }
  if (level <= PHRASE_LEVEL_SENTENCE) {
    return [nWords]
  }
  if (level >= PHRASE_LEVEL_WORDS) {
    return Array(nWords).fill(1) as number[]
  }
  return sentenceToSegmentation(sentence, PHRASERS.BMT, level)
}

// ─── Dynamic level discovery ────────────────────────────────────────

interface PhraseLevelInfo {
  level: number
  segmentCount: number
}

/**
 * Compute which phrase levels (0–5) produce *distinct* segmentations for
 * the given sentence.  Levels that yield the same split as an earlier
 * level are skipped, so the slider only shows meaningful stops.
 *
 * Returns an array sorted by level, each entry carrying the segment
 * count so the UI can label ticks (e.g. "2 phrases", "5 phrases").
 */
const availablePhraseLevels = (sentence: SentenceType, nWords: number): PhraseLevelInfo[] => {
  if (!sentence.text || nWords === 0) {
    return []
  }
  const result: PhraseLevelInfo[] = []
  let prevKey = ''
  for (let level = PHRASE_LEVEL_SENTENCE; level <= PHRASE_LEVEL_WORDS; level++) {
    const seg = segmentationForLevel(sentence, nWords, level)
    const key = JSON.stringify(seg)
    if (key !== prevKey) {
      result.push({ level, segmentCount: seg.length })
      prevKey = key
    }
  }
  return result
}

/**
 * Find the nearest available level to `target` from a list of PhraseLevelInfo.
 * Ties break toward the lower (coarser) level.
 */
const nearestAvailableLevel = (available: PhraseLevelInfo[], target: number): number => {
  if (!available.length) {
    return PHRASE_LEVEL_SENTENCE
  }
  let best = available[0]
  for (const info of available) {
    if (Math.abs(info.level - target) < Math.abs(best.level - target)) {
      best = info
    }
  }
  return best.level
}

// ─── Composable (used by ParseView for interactive debugging) ───────

const useVersePhraser = (logger: VueLogger) => {
  const phrasers = computed(() => [
    { title: 'BMT', value: PHRASERS.BMT },
    { title: 'default', value: PHRASERS.DEFAULT },
  ])

  const sentenceToSegmentationLogged = (
    sentence: SentenceType,
    method: PHRASERS = PHRASERS.BMT,
    maxLevel: number = DEFAULT_TROPE_LEVEL,
  ): SegmentationType => {
    const result = sentenceToSegmentation(sentence, method, maxLevel)
    logger.debug('sentenceToSegmentation result:', result)
    return result
  }

  return { phrasers, sentenceToSegmentation: sentenceToSegmentationLogged }
}

export type { PhraseLevelInfo }
export {
  availablePhraseLevels,
  DEFAULT_TROPE_LEVEL,
  nearestAvailableLevel,
  PHRASE_LEVEL_SENTENCE,
  PHRASE_LEVEL_WORDS,
  PHRASERS,
  segmentationForLevel,
  sentenceToSegmentation,
  useVersePhraser,
}
